import { addHours, addMinutes } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import type {
  ConstraintListItemDto,
  CreateEventsRequest,
  EventCategoryDto,
  EventDto,
  GroupDto,
  UserDto,
} from "@/api/types";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadioCard } from "@/components/Form/FormRadioCard";
import { FormScheduleInput } from "@/components/Form/FormScheduleInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { WithAppPreviewLayout } from "@/components/Layouts/WithAppPreviewLayout";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Phone } from "@/components/Phone/Phone";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { getEndDateAfterStartChange } from "@/helpers/date-range";
import { getEventCategoryName } from "@/helpers/events";
import { getFlagIcon } from "@/helpers/flags";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useInterval } from "@/hooks/useInterval";
import { useKey } from "@/hooks/useKey";
import { useSlug } from "@/hooks/useSlug";
import { AppEventDetailsView } from "@/modules/events/pages/event-editor/components/AppEventDetailsView";
import { EventTypeModal } from "@/modules/events/pages/event-editor/components/EventTypeModal";
import { useConfig } from "@/providers/config/useConfig";
import { routes } from "@/routes";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";

const MIN_AMOUNT_RECURRING = 1;
const MAX_AMOUNT_RECURRING = 100;

export interface EventObserver {
  type: "project" | "projectConnection";
  id: string;
  name: string;
}

export interface EventFormProps {
  contentLanguageId: Languages | undefined;
  defaultFormValues: CreateOrEditEventFormValues;
  isSubmitting: boolean;
  onSubmit: (formValues: CreateOrEditEventFormValues) => void;
  showAnnouncementModal: boolean;
  eventCategories: EventCategoryDto[];
  isResident: boolean;
  organizer: UserDto;
  isPublished: boolean;
  eventObservers: EventObserver[];
  isEditMode: boolean;
  /** Controls whether the event audience controls are rendered. */
  canEditAudience: boolean;
}

export function EventForm(props: EventFormProps): React.ReactNode {
  const [isEventRecurring, setIsEventRecurring] = useState(false);
  const [isCancelModalOpen, cancelModalHandler] = useBool(false);
  const [appPreviewOpen, appPreviewOpenHandlers] = useBool();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const { id: eventId } = useParams<{
    id: string;
  }>();
  useKey("Escape", appPreviewOpenHandlers.setFalse, appPreviewOpen);
  const formMethods = useForm<CreateOrEditEventFormValues>({
    mode: "onChange",
    defaultValues: props.defaultFormValues,
  });
  const slug = useSlug();
  const navigate = useNavigate();
  const useNewUnifiedPostCreation = useConfig("useNewUnifiedPostCreation");
  const withoutParticipantsIsPost = useNewUnifiedPostCreation && sessionUser.isAdmin;

  const audience = useWatch({
    control: formMethods.control,
    name: "audience",
  });
  const eventObserver = useWatch({
    control: formMethods.control,
    name: "eventObserver",
  });
  // Hoist the optional access: the React Compiler (oxc) strips the `?.` when it
  // lifts `eventObserver?.type` into a cache-dependency key, producing an
  // unguarded `eventObserver.type` that throws while the watched value is
  // undefined.
  const eventObserverType = eventObserver?.type;
  const start = useWatch({
    control: formMethods.control,
    name: "start",
  });
  const noParticipantsLimit = useWatch({
    control: formMethods.control,
    name: "noParticipantsLimit",
  });
  const participantCount = useWatch({
    control: formMethods.control,
    name: "participantCount",
  });
  const type = useWatch({
    control: formMethods.control,
    name: "type",
  });

  const [minScheduled] = useState(() => addMinutes(new Date(), 5));
  const { clearErrors, setValue } = formMethods;
  const [isTypeModalOpen, typeModalHandlers] = useBool(props.showAnnouncementModal);
  const endBeforeStartAdjustment = useRef<{ end: Date | undefined } | null>(null);

  const handleStartChange = (nextStart: Date | undefined) => {
    const previousEnd = formMethods.getValues("end");
    const end = getEndDateAfterStartChange(start, previousEnd, nextStart, (date) => addHours(date, 1));
    if (end?.getTime() !== previousEnd?.getTime() && endBeforeStartAdjustment.current == null) {
      endBeforeStartAdjustment.current = { end: previousEnd };
    }
    setValue("end", end, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (eventObserverType === "projectConnection") {
      setValue("audience", []);
    }
  }, [eventObserverType, setValue]);

  const handleChangeRecurringStatus = (isRecurring: boolean) => {
    if (isRecurring) {
      setValue("recurrence", "daily");
    } else {
      setValue("recurrence", undefined);
      setValue("recurrenceCount", props.defaultFormValues.recurrenceCount.toString());
    }

    setIsEventRecurring(isRecurring);
  };

  const handleCapacityModeChange = (mode: "limited" | "unlimited") => {
    const isUnlimited = mode === "unlimited";
    setValue("noParticipantsLimit", isUnlimited, { shouldDirty: true, shouldValidate: true });
    if (isUnlimited) {
      clearErrors("maxParticipants");
    }
  };

  const getRecurrenceOptionLabel = (recurrence: Exclude<CreateEventsRequest["recurrence"], undefined>) => {
    switch (recurrence) {
      case "weekly":
        return t("common.label.recurrence.weekly");
      case "biweekly":
        return t("common.label.recurrence.biweekly");
      case "monthly":
        return t("common.label.recurrence.monthly");
      case "daily":
      default:
        return t("common.label.recurrence.daily");
    }
  };

  const [minStart] = useState(() => addMinutes(new Date(), 15));
  const currentDate = useCurrentDate();
  const originalStart = props.defaultFormValues.start;
  const hasStarted = props.isEditMode && originalStart != null && originalStart <= currentDate;
  const restoreOriginalStart = () => {
    setValue("start", originalStart, { shouldValidate: true });
    const adjustment = endBeforeStartAdjustment.current;
    if (adjustment != null) {
      setValue("end", adjustment.end, { shouldValidate: true });
      endBeforeStartAdjustment.current = null;
    }
  };
  useInterval(() => {
    if (!props.isEditMode || originalStart == null || originalStart > new Date()) {
      return;
    }
    if (formMethods.getValues("start")?.getTime() !== originalStart.getTime()) {
      restoreOriginalStart();
    }
  }, 1_000);
  const title = eventId
    ? t("page.event.create-or-edit.edit-mode.title")
    : t("page.event.create-or-edit.create-mode.title");
  const flagIcon = getFlagIcon({
    languageId: props.contentLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.calendar.title"),
              to: routes.calendar.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root
        formMethods={formMethods}
        onSubmit={(values) => {
          if (props.isEditMode && originalStart != null && originalStart <= new Date()) {
            if (values.start?.getTime() !== originalStart.getTime()) {
              restoreOriginalStart();

              return;
            }
            props.onSubmit({ ...values, start: originalStart });
          } else {
            props.onSubmit(values);
          }
        }}
      >
        <WithAppPreviewLayout
          preview={<Preview eventId={eventId} organizer={props.organizer} eventCategories={props.eventCategories} />}
        >
          <Form.Container>
            <Form.Body>
              <Notice
                icon={<Translate01Icon />}
                variant="special"
                message={
                  props.isEditMode
                    ? t("common.label.you-are-editing-in-original-content-language")
                    : t("common.label.your-input-will-be-auto-translated")
                }
              />
              <FormField label={t("model.event.category")} description={t("model.event.category.description")} required>
                <Tiles columns={2} gap="sm">
                  {props.eventCategories.map((currEventCategory) => (
                    <FormRadioCard<CreateOrEditEventFormValues, "category">
                      key={currEventCategory.id}
                      label={getEventCategoryName(t, currEventCategory.id)}
                      name="category"
                      value={currEventCategory.id}
                    />
                  ))}
                </Tiles>
              </FormField>
              {type === "common" && (
                <FormField
                  label={t("model.event.max-participants")}
                  description={t("model.event.max-participants.description")}
                >
                  <Stack gap="sm" align="start">
                    <SegmentGroup
                      width="content"
                      ariaLabel={t("model.event.max-participants")}
                      value={noParticipantsLimit ? "unlimited" : "limited"}
                      onChange={handleCapacityModeChange}
                      items={[
                        {
                          value: "unlimited",
                          label: t("model.event.max-participants.mode.unlimited"),
                          "data-testid": "event-unlimited-participants-input",
                        },
                        {
                          value: "limited",
                          label: t("model.event.max-participants.mode.limited"),
                          "data-testid": "event-limited-participants-input",
                        },
                      ]}
                    />
                    {!noParticipantsLimit && (
                      <FormInput<CreateOrEditEventFormValues>
                        aria-label={t("model.event.max-participants")}
                        data-testid="event-max-participants-input"
                        inputMode="numeric"
                        type="number"
                        step={1}
                        name="maxParticipants"
                        width="numeric"
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.event.max-participants"),
                            }),
                            value: !noParticipantsLimit,
                          },
                          min: {
                            message: t("model.event.max-participants.error.min"),
                            value: participantCount,
                          },
                          validate: (value) =>
                            value == null ||
                            value === "" ||
                            Number.isInteger(Number(value)) ||
                            t("model.event.max-participants.error.integer"),
                        }}
                      />
                    )}
                  </Stack>
                </FormField>
              )}
              {type === "common" && (
                <FormField
                  label={t("model.event.allow-guests")}
                  description={t("model.event.allow-guests.description")}
                >
                  <FormCheckbox<CreateOrEditEventFormValues>
                    data-testid="event-allow-guests-input"
                    name="allowGuests"
                    label={t("model.event.allow-guests.label")}
                  />
                </FormField>
              )}
              {props.canEditAudience && (
                <FormField
                  label={t("common.entity.audience")}
                  description={t("component.audience-section.description", {
                    audienceTarget: t("common.entity.event").toLowerCase(),
                  })}
                >
                  <Stack gap="sm">
                    <FormSelect<CreateOrEditEventFormValues, EventObserver>
                      name="eventObserver"
                      items={props.eventObservers}
                      keySelector={(item) => item.type + "-" + item.id}
                      renderOption={(item) => item.name}
                      groupSelector={(item) => {
                        switch (item.type) {
                          case "project":
                            return t("model.event.audience.group.project");
                          case "projectConnection":
                            return t("model.event.audience.group.area");
                        }
                      }}
                      rules={{
                        required: t("components.form.error.required", {
                          inputName: t("page.message-feed.create.audience"),
                        }),
                      }}
                      disabled={props.isEditMode || sessionUser.connections.length === 0}
                    />
                    {!props.isResident && eventObserverType !== "projectConnection" && (
                      <AudienceSelector
                        defaultAudience={audience}
                        isEditMode={props.isEditMode}
                        editWarningMessage={t("component.audience-selector.edit-warning.event")}
                        onSave={(audience) => formMethods.setValue("audience", audience)}
                      />
                    )}
                  </Stack>
                </FormField>
              )}
              <FormField label={t("model.event.name")} required actions={flagIcon}>
                <FormInput<CreateOrEditEventFormValues>
                  data-testid="event-name-input"
                  name="name"
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "model.event.name"),
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", {
                        length: NAME_MAX_LENGTH,
                      }),
                      value: NAME_MAX_LENGTH,
                    },
                  }}
                  placeholder={t("model.event.name.placeholder")}
                />
              </FormField>
              <FormField label={t("model.event.description")} actions={flagIcon} required>
                <FormTextArea<CreateOrEditEventFormValues>
                  data-testid="event-description-input"
                  name="description"
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "model.event.description"),
                    },
                  }}
                  placeholder={t("model.event.description.placeholder")}
                />
              </FormField>
              <FormField label={t("model.event.image")} description={t("model.event.image.description")}>
                <FormImageInput<CreateOrEditEventFormValues, "image">
                  layer="raised"
                  data-testid="event-image-input"
                  name="image"
                  rules={{
                    validate: {
                      size: createImageSizeRule({
                        t,
                      }),
                      type: createImageTypeRule({
                        t,
                      }),
                    },
                  }}
                />
              </FormField>
              <FormField label={t("model.event.time-start")} required>
                <FormDatePicker<CreateOrEditEventFormValues, "start">
                  data-testid="event-start-time"
                  name="start"
                  disabled={hasStarted}
                  onChange={handleStartChange}
                  type="datetime"
                  placeholder={t("model.event.time-start.placeholder")}
                  min={minStart}
                  showNow={false}
                  rules={{
                    validate: {
                      laterThanMin: (startDate) => {
                        if (startDate == null) {
                          return undefined;
                        }

                        if (props.isEditMode && startDate.getTime() === originalStart?.getTime()) {
                          return undefined;
                        }

                        return startDate < minStart ? t("model.event.start-date.error.must-be-in-future") : undefined;
                      },
                    },
                    required: {
                      message: t("components.form.error.required", {
                        inputName: t("model.event.time-start"),
                      }),
                      value: true,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("model.event.time-end")} required>
                <FormDatePicker<CreateOrEditEventFormValues>
                  data-testid="event-end-time"
                  name="end"
                  onChange={() => {
                    endBeforeStartAdjustment.current = null;
                  }}
                  type="datetime"
                  min={start ? addMinutes(start, 1) : minStart}
                  placeholder={t("model.event.time-end.placeholder")}
                  showNow={false}
                  rules={{
                    validate: {
                      laterThanStart: (endDate, formValues) => {
                        if (endDate == null) {
                          return undefined;
                        }

                        return formValues.start && endDate <= formValues.start
                          ? t("model.event.end-date.error.later-than-start-date")
                          : undefined;
                      },
                    },
                    required: {
                      message: t("components.form.error.required", {
                        inputName: t("model.event.time-end"),
                      }),
                      value: true,
                    },
                  }}
                />
              </FormField>
              {!props.isEditMode && (
                <>
                  <Checkbox
                    data-testid="event-recurring-checkbox"
                    label={t("page.event.create-or-edit.is-recurring.label")}
                    checked={isEventRecurring}
                    onChange={(e) => handleChangeRecurringStatus(e.target.checked)}
                  />
                  {isEventRecurring && (
                    <section className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>{t("page.event.create-or-edit.amount-recurrence.label.1")}</span>
                      <FormSelect<CreateOrEditEventFormValues, Exclude<CreateEventsRequest["recurrence"], undefined>>
                        data-testid="recurrence-select-container"
                        name="recurrence"
                        items={["daily", "weekly", "biweekly", "monthly"]}
                        keySelector={(item) => item}
                        renderOption={(item) => getRecurrenceOptionLabel(item)}
                        rules={{
                          required: t("components.form.error.required-generic"),
                        }}
                        disabled={props.isEditMode}
                      />
                      <span>{t("page.event.create-or-edit.amount-recurrence.label.2")}</span>
                      <FormInput<CreateOrEditEventFormValues, "recurrenceCount">
                        data-testid="amount-recurrence-input"
                        name="recurrenceCount"
                        placeholder={MIN_AMOUNT_RECURRING.toString()}
                        type="number"
                        inputMode="numeric"
                        min={MIN_AMOUNT_RECURRING}
                        max={MAX_AMOUNT_RECURRING}
                        rules={{
                          valueAsNumber: true,
                          validate: {
                            min(value) {
                              if (parseInt(value) < MIN_AMOUNT_RECURRING) {
                                return t("page.event.create-or-edit.amount-recurrence.validation.min", {
                                  min: MIN_AMOUNT_RECURRING,
                                });
                              }

                              return undefined;
                            },
                            max(value) {
                              if (parseInt(value) > MAX_AMOUNT_RECURRING) {
                                return t("page.event.create-or-edit.amount-recurrence.validation.max", {
                                  max: MAX_AMOUNT_RECURRING,
                                });
                              }

                              return undefined;
                            },
                          },
                        }}
                        width="numeric"
                      />
                      <span>{t("page.event.create-or-edit.amount-recurrence.label.3")}</span>
                    </section>
                  )}
                </>
              )}
              <FormField label={t("page.event.create-or-edit.location")}>
                <FormInput<CreateOrEditEventFormValues>
                  data-testid="event-location-input"
                  type="text"
                  name="location"
                  placeholder={t("page.event.create-or-edit.location.placeholder")}
                />
              </FormField>

              {!props.isPublished && sessionUser.isAdmin ? (
                <FormField label={t("page.event.create-or-edit.publish")}>
                  <FormScheduleInput
                    name="publishAt"
                    min={minScheduled}
                    max={start ? start : undefined}
                    canEdit={!eventId || !props.isPublished}
                    rules={{
                      validate: {
                        mustBeBeforeStart: (date) => {
                          if (!date || !start) {
                            return undefined;
                          }

                          return date >= start
                            ? t("page.event.create-or-edit.publish.validation.must-be-before-live")
                            : undefined;
                        },
                        laterThanMin: (date) => {
                          if (!date) {
                            return undefined;
                          }

                          return date < new Date()
                            ? t("page.event.create-or-edit.publish.validation.must-be-in-future")
                            : undefined;
                        },
                      },
                    }}
                  />
                </FormField>
              ) : null}
            </Form.Body>
            {/* Actions */}
            <Form.Footer
              renderPrimaryAction={() => (
                <Button data-testid="submit-event-btn" variant="filled" type="submit" isLoading={props.isSubmitting}>
                  {t("common.action.save")}
                </Button>
              )}
              renderSecondaryAction={() => (
                <Button
                  variant="subtle"
                  tone="error"
                  disabled={props.isSubmitting}
                  onClick={cancelModalHandler.setTrue}
                >
                  {t("common.action.cancel")}
                </Button>
              )}
            />
          </Form.Container>
        </WithAppPreviewLayout>
      </Form.Root>
      <ConfirmModal
        tone="primary"
        isOpened={isCancelModalOpen}
        data-testid="cancel-modal"
        title={t("page.event.create-or-edit.cancel-modal.title")}
        description={t("page.event.create-or-edit.cancel-modal.description")}
        shouldCloseOnEsc
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={() =>
          void navigate(
            routes.bookings.list({
              slug,
            }),
          )
        }
        resolveBtnProps={{
          text: t("common.action.confirm"),
        }}
        isLoading={false}
      />
      <EventTypeModal
        isOpened={isTypeModalOpen}
        onOpenChange={typeModalHandlers.set}
        withoutParticipantsIsPost={withoutParticipantsIsPost}
        onContinue={(type) => {
          if (type === "announcement" && withoutParticipantsIsPost) {
            void navigate(routes.communityFeed.adminCreatePost({ slug }));

            return;
          }

          setValue("type", type);
          typeModalHandlers.setFalse();
        }}
      />
    </DocumentPage>
  );
}

function Preview({
  eventId,
  organizer,
  eventCategories,
}: {
  eventId?: string;
  organizer: UserDto;
  eventCategories: EventCategoryDto[];
}) {
  const form = useFormContext<CreateOrEditEventFormValues>();
  const sessionUser = useSessionUser();
  const {
    name,
    description,
    start,
    end,
    location,
    noParticipantsLimit,
    maxParticipants,
    participantCount,
    type,
    category,
    image,
    eventObserver,
  } = useWatch<CreateOrEditEventFormValues>({
    control: form.control,
  });

  return (
    <Phone>
      <AppEventDetailsView
        name={name?.slice(0, NAME_MAX_LENGTH) || ""}
        description={description || ""}
        iconChar={eventCategories.find((c) => c.id === category)?.iconChar}
        maxParticipants={+(maxParticipants ?? 0)}
        startDate={start}
        endDate={end}
        image={image?.[0]?.url ?? sessionUser.project?.backgroundImage?.url}
        location={location || ""}
        participantCount={eventId ? participantCount || 0 : 0}
        isAnnouncement={type === "announcement"}
        projectConnection={eventObserver?.type === "projectConnection" ? eventObserver?.name : undefined}
        category={eventCategories.find((c) => c.id === category)}
        noParticipantsLimit={noParticipantsLimit}
        organizer={organizer}
        projectTintColor={sessionUser.project?.styling.tintColor}
        isEditMode={!!eventId}
      />
    </Phone>
  );
}
const NAME_MAX_LENGTH = 40;

export interface CreateOrEditEventFormValues {
  type: EventDto["type"];
  category: EventCategoryDto["id"];
  name: string;
  description: string;
  start: Date | undefined;
  end: Date | undefined;
  location?: string;
  image: FormImage[];
  groups?: GroupDto[];
  maxParticipants?: string;
  noParticipantsLimit: boolean;
  allowGuests: boolean;
  participantCount: number;
  audience: ConstraintListItemDto[];
  eventObserver: EventObserver;
  publishAt: Date | undefined;
  recurrenceCount: string;
  recurrence?: CreateEventsRequest["recurrence"];
}
